// material
import { alpha, styled, useTheme } from "@mui/material/styles";
import {
  Card,
  CardContent,
  FormControl,
  Stack,
  Typography,
  Box,
  Skeleton,
} from "@mui/material";
import LocalGasStationRoundedIcon from "@mui/icons-material/LocalGasStationRounded";
import EvStationRoundedIcon from "@mui/icons-material/EvStationRounded";
import PowerSettingsNewRoundedIcon from "@mui/icons-material/PowerSettingsNewRounded";
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from "recharts";
// utils
import { fShortenNumber, fCurrency } from "../../../utils/formatNumber";
import { useMemo } from "react";
import MetricStatCard from "src/components/MetricStatCard";

// ----------------------------------------------------------------------

const RootStyle = styled(Card)(({ theme }) => ({
  boxShadow: "none",
  textAlign: "center",
  padding: theme.spacing(5, 0),
  color: theme.palette.primary.darker,
  backgroundColor: theme.palette.primary.lighter,
}));

const RootStyleNew = styled(Card)(
  ({ theme, maxwidth, minheight = "100%", backgroundcolor, color }) => ({
    maxWidth: maxwidth,
    minHeight: minheight,
    boxShadow: "none",
    backgroundColor: backgroundcolor,
    color: color,
    padding: theme.spacing(2),
    display: "grid",
    gap: "5px 0",
  })
);

const elevatedSurface = (theme) => ({
  backgroundColor:
    theme.palette.mode === "light"
      ? "#FFFFFF"
      : theme.palette.background.elevated,
  border:
    theme.palette.mode === "light"
      ? "1px solid transparent"
      : `1px solid ${alpha(theme.palette.divider, 0.8)}`,
  boxShadow:
    theme.palette.mode === "light"
      ? "0px 4px 12px rgba(0, 0, 0, 0.1)"
      : "0px 16px 32px rgba(0, 0, 0, 0.55)",
});

const RootStyleMapCard = styled(Card)(({ theme }) => {
  const isLight = theme.palette.mode === "light";
  const backgroundColor = isLight
    ? theme.palette.background.paper
    : theme.palette.background.elevated || alpha(theme.palette.background.paper, 0.12);
  const borderColor = alpha(theme.palette.divider, isLight ? 0.6 : 0.4);
  const radius = theme.shape.borderRadius * 3;
  const boxShadow = isLight
    ? "0 18px 35px rgba(15,23,42,0.08)"
    : "0 28px 45px rgba(2,6,23,0.55)";

  return {
    width: "100%",
    height: "100%",
    borderRadius: radius,
    backgroundColor,
    border: `1px solid ${borderColor}`,
    color: theme.palette.text.primary,
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    boxShadow,
    overflow: "hidden",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: isLight
        ? "0 22px 42px rgba(15,23,42,0.12)"
        : "0 32px 52px rgba(2,6,23,0.65)",
    },
  };
});

export const StyledFormControl = styled(FormControl)(
  ({ theme, bgcolor, maxwidth = "100px" }) => {
    return {
      background: `linear-gradient(35deg, ${theme.lighter} 10%, ${theme.light} 60%, ${theme.main} 88%, ${bgcolor} 100%)`,
      borderRadius: "5px",
      maxWidth: maxwidth,
    };
  }
);

const IconWrapperStyle = styled("div")(({ theme }) => ({
  margin: "auto",
  display: "flex",
  borderRadius: "50%",
  alignItems: "center",
  width: theme.spacing(8),
  height: theme.spacing(8),
  justifyContent: "center",
  marginBottom: theme.spacing(3),
  color: theme.palette.primary.dark,
  backgroundImage: `linear-gradient(135deg, ${alpha(
    theme.palette.primary.dark,
    0
  )} 0%, ${alpha(theme.palette.primary.dark, 0.24)} 100%)`,
}));

// ----------------------------------------------------------------------

export default function DashboardCard({
  title,
  count,
  icon,
  colorTheme,
  unit = "",
}) {
  return (
    <RootStyle
      sx={{ backgroundColor: colorTheme?.lighter, color: colorTheme?.darker }}
    >
      <IconWrapperStyle
        sx={{
          color: colorTheme?.dark,
          backgroundImage: `${
            colorTheme
              ? `linear-gradient(135deg, ${alpha(
                  colorTheme?.dark,
                  0
                )} 0%, ${alpha(colorTheme?.dark, 0.24)} 100%)`
              : undefined
          }`,
        }}
      >
        {icon}
      </IconWrapperStyle>
      <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
        {title}
      </Typography>
      <Typography variant="h3">
        {count === "N/A" ? "N/A" : typeof count === "number" ? fCurrency(count) : count}
        {unit ? " " + unit : ""}
      </Typography>
    </RootStyle>
  );
}

export const DashboardNumberStatsCard = ({
  title,
  count,
  footer,
  percentChange,
  colorTheme,
  isFetching,
  isLive,
  paletteKey,
}) => {
  const normalizedPercent =
    typeof percentChange === "string" ? percentChange.trim() : "";
  const hasTrend = normalizedPercent.length > 0;
  const normalizedFooter =
    footer !== null && footer !== undefined
      ? footer.toString().trim()
      : "";
  const accentKey =
    paletteKey || (typeof colorTheme === "string" ? colorTheme : "secondary");
  const trendDirection = hasTrend && normalizedPercent.toLowerCase().includes("decrease")
    ? "down"
    : "up";

  return (
    <MetricStatCard
      title={title}
      value={count}
      secondaryText={normalizedFooter || undefined}
      badgeText={isLive ? "LIVE" : undefined}
      trendText={hasTrend ? normalizedPercent : undefined}
      trendDirection={trendDirection}
      accent={accentKey}
      isLoading={Boolean(isFetching)}
    />
  );
};

export const DashboardDonutStatsCard = ({
  totalDevices = 0,
  onlineDevices = 0,
  offlineDevices = 0,
  isLoading = false,
}) => {
  const theme = useTheme();
  const safeTotal = Number.isFinite(totalDevices) ? Math.max(totalDevices, 0) : 0;
  const safeOnline = Number.isFinite(onlineDevices) ? Math.max(onlineDevices, 0) : 0;
  const safeOffline = Number.isFinite(offlineDevices) ? Math.max(offlineDevices, 0) : 0;
  const computedTotal = Math.max(safeTotal, safeOnline + safeOffline, 0);
  const denominator = computedTotal > 0 ? computedTotal : 1;
  const offlinePercent = Math.min(100, Math.max(0, (safeOffline / denominator) * 100));
  const offlineLabel = `${offlinePercent.toFixed(1)}%`;
  const rowBg =
    theme.palette.mode === "light"
      ? alpha(theme.palette.common.black, 0.03)
      : alpha(theme.palette.primary.main, 0.06);
  const iconOpacity = theme.palette.mode === "dark" ? 0.85 : 1;
  const metrics = [
    {
      label: "Total Devices",
      value: safeTotal,
      Icon: LocalGasStationRoundedIcon,
      color: theme.palette.primary.main,
    },
    {
      label: "Online Devices",
      value: safeOnline,
      Icon: EvStationRoundedIcon,
      color: theme.palette.success.main,
    },
    {
      label: "Offline Devices",
      value: safeOffline,
      Icon: PowerSettingsNewRoundedIcon,
      color: theme.palette.error.main,
    },
  ];

  const chartData = useMemo(
    () => [
      { name: "Online", value: safeOnline, color: theme.palette.success.main },
      { name: "Offline", value: safeOffline, color: theme.palette.error.main },
    ],
    [safeOnline, safeOffline, theme.palette.error.main, theme.palette.success.main]
  );

  const formatValue = (value) =>
    Number.isFinite(value) ? value.toLocaleString() : "0";

  return (
    <Card
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: "16px",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${alpha(theme.palette.divider, theme.palette.mode === "light" ? 0.5 : 0.4)}`,
        padding: { xs: 2, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 2,
        transition: "box-shadow 0.3s ease, transform 0.3s ease",
        boxShadow:
          theme.palette.mode === "light"
            ? "0 15px 35px rgba(15,23,42,0.08)"
            : "0 25px 45px rgba(0,0,0,0.55)",
        "&:hover": {
          boxShadow:
            theme.palette.mode === "light"
              ? "0 25px 45px rgba(15,23,42,0.12)"
              : "0 35px 65px rgba(0,0,0,0.75)",
          transform: "translateY(-2px)",
        },
      }}
    >
      <Stack spacing={1.5}>
        {isLoading
          ? [0, 1, 2].map((item) => (
              <Skeleton key={item} variant="rounded" height={64} sx={{ borderRadius: 2 }} />
            ))
          : metrics.map(({ label, value, Icon, color }) => (
              <Box
                key={label}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: rowBg,
                  borderRadius: 2,
                  padding: theme.spacing(1.5, 2),
                }}
              >
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary, letterSpacing: 0.3, textTransform: "uppercase" }}
                  >
                    {label}
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ color: theme.palette.text.primary, fontWeight: 600, marginTop: 0.5 }}
                  >
                    {formatValue(value)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: alpha(color, theme.palette.mode === "light" ? 0.12 : 0.22),
                    color,
                    opacity: iconOpacity,
                  }}
                >
                  <Icon fontSize="small" />
                </Box>
              </Box>
            ))}
      </Stack>

      <Box sx={{ width: "100%", flexGrow: 1, position: "relative", minHeight: 220 }}>
        {isLoading ? (
          <Skeleton
            variant="circular"
            width={180}
            height={180}
            sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}
          />
        ) : (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <RechartsPieChart>
                <Pie
                  data={chartData}
                  cx="50%"
                  cy="50%"
                  innerRadius="65%"
                  outerRadius="85%"
                  dataKey="value"
                  stroke="none"
                  paddingAngle={chartData.some(({ value }) => value > 0) ? 2 : 0}
                >
                  {chartData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
              </RechartsPieChart>
            </ResponsiveContainer>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                textAlign: "center",
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                {offlineLabel}
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Offline
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Card>
  );
};

export const DashboardMapCard = ({ title, bgColor, fontColor, children, cardHeight, sx }) => {
  const theme = useTheme();
  const resolvedMinHeight = Number.isFinite(cardHeight) ? cardHeight : undefined;
  return (
    <RootStyleMapCard sx={{ minHeight: resolvedMinHeight, ...sx }}>
      <Box sx={{ px: { xs: 2, md: 2.5 }, pt: { xs: 2, md: 2.5 }, pb: 1.5 }}>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: "bolder",
            fontSize: "1.13rem",
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
      </Box>
      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            width: '100%',
            height: '100%',
            minHeight: 320,
            position: 'relative',
          }}
        >
          {children}
        </Box>
      </CardContent>
    </RootStyleMapCard>
  );
};